import { FC } from 'react';
import { MemoizedHbtDataTableCellProps } from 'Feature/V2/Shared/MemoizedHbtDataTableCell/types';
import { TFunction } from 'i18next';
import { PortfolioLoanRecordStatusCode } from '../../api/types';
import { LoanInventoryTableFields, RemarkDetail } from '../HbtPortfolioLoansInventoryTable/types';

export type LoanStatusType = '01' | '02' | '03' | '04' | '09';

export interface IndividualLoanDetailsFields {
  portfolioID: string;
  portfolioStatus: string;
  loanReferenceNumber: string;
  loanStatus: LoanStatusType;
}

export interface InsuredApprovedLenderName {
  englishName: string;
  frenchName: string;
}

export interface Address {
  municipalityName: string;
  postalCode: string;
  provinceID: string;
  streetDirectionID: string;
  streetName: string;
  streetNumber: string;
  streetTypeID: string;
  unitNumber: string;
  isSystemPopulated?: boolean;
}

export interface LenderDetails {
  insuredApprovedLenderName: InsuredApprovedLenderName;
  underwritinApprovedLenderName: InsuredApprovedLenderName;
}

export interface LoanBorrower {
  typeId: number;
  lastName?: string;
  firstName?: string;
  address?: Address;
  dateOfBirth?: string | undefined;
  creditScore?: string | undefined;
  risk?: string | undefined;
  totalIncome?: number;
}

interface RemarksDetail {
  remarkId?: number;
  remarkText?: string;
  recordUpdatedTimestamp?: string;
  recordCreatedTimestamp?: string;
  recordCreatedUserID?: string;
  firstName?: string;
  lastName?: string;
}

export interface LoanDetails {
  id: string;
  lenderReferenceNumber: string; // lenderLoanNumber
  loanStatus: string;
  loanStatusEffectiveDate: string; // lastStatusUpdateTimeStamp
  lowRatioCmhcAccountNumber: number | null;

  // portfolio
  portfolioId: number;
  insuredApprovedLenderCode: string | undefined; // insuredLenderCode
  insureApprovedLender: InsuredApprovedLenderName;
  underwritingApprovedLenderCode?: string | undefined; // loanUnderwritingLenderCode
  underwritingApprovedLender: InsuredApprovedLenderName;
  dataSetReducedIndicator: string;

  // borrowers details
  borrowers: LoanBorrower[]; // borrower (typeID=1) and coBorrower (typeID=2)

  // property
  propertyAddress: Address;
  propertyUrbanRuralIndicator: string;
  propertyNoOfUnits: number;
  propertyDwellingType: string;
  propertyTenureType: string;
  propertyOccupancyType: string;
  propertyLegalDescription: string;

  // loan details
  loanOriginalLendingValue: number | undefined;
  loanOriginalAmount: number | undefined;
  loanCurrentAmount: number | undefined;
  loanOriginalLtvRatio: number | undefined;
  loanCurrentLtvRatio: number | undefined;
  loanInterestAdjustmentDate: string | undefined;
  loanTermExpiryDate: string | undefined;
  loanTerm: string;
  loanCurrentInterestRate: number | undefined;
  loanInterestType: string;
  loanGrandfatheredIndicator: string;
  loanQualifyingTotalDebtService: number | undefined;
  loanQualifyingGrossDebtService: number | undefined;
  loanPurposeType: string | undefined;
  loanOriginalAmortization: string | undefined;
  loanRemainingAmortization: string | undefined;
  loanPaymentResetFrequency: number | undefined;

  // other
  otherMultiComponentKey: string;
  otherMortgageSecurityPriorityRanking: string | undefined;
  mortgageSecurityType: string;
  propertyValuationMethod: string;
  propertyValuationMethodOtherDescription: string;
  mortgagePaymentFrequency: string;
  mortgagePrincipalAndInterestPaymentAmountNoTax: number;
  residentialStatus: string;
  propertyListingType: string;
  sourceOfBusiness: string | undefined;
  brokerMortgageSpecialistLastName: string | undefined;
  brokerMortgageSpecialistFirstName: string | undefined;
  loanReviewTimestamp?: string | undefined;

  // low ratio details
  lowRatioIndicator?: string | undefined;
  lastReferredDate?: string | undefined;
  lowRatioNumber?: number | undefined;
  lowRatioResults?: string | undefined;
  lowRatioPropertyRisk?: string | undefined;
  lowRatioApplicationRisk?: string | undefined;
  lowRatioBorrowers?: LoanBorrower[];
  lowRatioAge?: number | undefined;
  lowRatioPropertyAddress?: Address | undefined;
  lowRatioTenureType?: string | undefined;
  lowRatioDwellingType?: string | undefined;
  lowRatioNumberOfUnits?: number | undefined;
  lowRatioLoanPurposeType?: string | undefined;
  lowRatioOriginalLendingValue?: number | undefined;
  lowRatioOriginalLoanAmount?: number | undefined;
  borrowerCreditScore?: string | undefined;
  coBorrowerCreditScore?: string | undefined;

  // loan results and remarks
  errorMessages: string[];

  // latest remark
  latestRemark?: RemarkDetail;
  remarks?: RemarksDetail[];
  borrowerRisk?: string | undefined;
  coBorrowerRisk?: string | undefined;
  coBorrowerBirthDate?: string | undefined;
  borrowerBirthDate?: string | undefined;
}

export type Borrower = {
  name: string;
  dateOfBirth: string;
  address: string;
  creditScore: string;
  totalIncome: string;
};

export type BorrowerExtends = Borrower & {
  lenderCreditScore: string;
  CMHCCreditScore: string;
};
export interface PropertyLoanDetails {
  address: string;
  locationType: string;
  occupancyType: string;
  tenureType: string;
  noOfUnits: string;
  dwellingType: string;
  legalDescription: string;
}
export interface KeyLoanDetails {
  originalLendingValue: string;
  originalLoanAmount: string;
  originalLTVRatio: string;
  currentLoanAmount: string;
  currentLTVRatio: string;
  interestAdjustmentDate: string;
  loanTerm: string;
  loanTermExpiryDate: string;
  interestType: string;
  grandfathered: string;
  loanPurposeType: string;
  qualifyingTotalDebtService: string;
  qualifyingGrossDebtService: string;
  originalAmortization: string;
  remainingAmortization: string;
  paymentResetFrequency: string;
  interestRate: string;
}

export interface CmhcLowRatioDataLoanDetails {
  underwritingLenderName: InsuredApprovedLenderName;
  accountNumber: string | undefined;
  lowRatioCmhcAccountNumber: number | null;
  lastReferedDate: string;
  lowRatioResults: string;
  result?: string;
  lowRatioAge: string;
  applicationRisk: string;
  propertyRisk: string;
  borrowerRisk: string;
  coborrowerRisk: string;
  borrowerDoB: string;
  coborrowerDoB: string;
  borrowerCreditScore: string;
  coborrowerCreditScore: string;
  propertyAddress: string;
  noOfUnits: string;
  dwellingType: string;
  tenureType: string;
  originalLoanAmount: string;
  originalLendingValue: string;
  loanPurposeType: string;
}

export interface CMHCLowRatioDataLoanDetailsExtends extends CmhcLowRatioDataLoanDetails {
  result: string;
}

export type DataColumnType = {
  name: string;
  displayName: string;
  t: Function;
};
export interface RequestDetails {
  portfolioId: string;
  insuredApprovedLenderName: InsuredApprovedLenderName;
  underwritingApprovedLenderName?: InsuredApprovedLenderName;
  underwritingLenderName?: InsuredApprovedLenderName;
}

export type HbtPortfolioIndividualLoanDetailsProps = {
  portfolioId: string;
  loanReferenceNumber: string;
  loansId: string;
  latestRemarks?: RemarkDetail;
  loanPortfolioEpipProcessingStatus?: string;
  loanInventoryData?: LoanInventoryTableFields;
  toggleToaster?: (error: boolean) => void;
};

export type TableRowProps = {
  leftTitle: string;
  leftValue: FC<MemoizedHbtDataTableCellProps>;
  rightTitle: string;
  rightValue: FC<MemoizedHbtDataTableCellProps>;
  className?: string;
  rightTitleTooltip?: string;
};

export interface OtherLoanDetails {
  multiComponentKey: string;
  mortgageSecurityPriorityRanking: string;
  mortgageSecurityType: string;
  propertyValuationMethod: string;
  propertyValuationMethodOther: string;
  mortgatePaymentFrequency: string;
  mortgatePrincipalAndInterestPaymentAmount: string;
  residentialStatus: string;
  propertyListingType: string;
  sourceOfBusiness: string;
  brokerMortgageSpecialistName: string;
}

export interface RequestDetailsSectionProps {
  isCollapsed: boolean;
  onToggle: () => void;
  lenderDetails: RequestDetails | undefined;
}
export interface PropertyCharacteristicsSectionProps {
  isCollapsed: boolean;
  onToggle: () => void;
  propertyData: PropertyLoanDetails | undefined;
  className: string;
}
export interface OtherLoanCharacteristicsSectionProps {
  isCollapsed: boolean;
  onToggle: () => void;
  otherLoanData: OtherLoanDetails | undefined;
}
export interface KeyLoanCharacteristicsSectionProps {
  isCollapsed: boolean;
  onToggle: () => void;
  keyLoanData: KeyLoanDetails | undefined;
  options?: {
    isToolTip: boolean;
    toolTipDesc: string;
  };
}
export interface CMHCLowRatioCharacteristicsSectionProps {
  isCollapsed: boolean;
  onToggle: () => void;
  cmhcLowRatioData: CmhcLowRatioDataLoanDetails | undefined;
  options: {
    LayoutContainerType: string;
    isReducedDataSet: boolean;
  };
}

export interface DecisionOverrideRationales {
  id: number;
  englishRationale: string;
  frenchRationale: string;
  type: RationaleType;
  domainTypeCode: string;
}
export interface DTODecisionOverrideRationales {
  id: number;
  englishRationale: string;
  frenchRationale: string;
  type: RationaleType;
  domainTypeCode: string;
}
export interface LoansDecisionOverridesRequest {
  poolprecitiontypecode?: string;
  validateOnly?: boolean;
  targetStatus?: string;
  cmhcAccountNumbers?: string[];
  portfolioIds?: number[];
  applicationId?: number;
  lenderReferenceNumber?: string[];
  decisionOverrideRationaleId?: string;
  decisionOverrideRemarks?: string;
}

export enum RationaleType {
  pass = 'pass',
  fail = 'fail'
}

export enum LoanStatus {
  pass = '02',
  fail = '03'
}

export type TranslationType = TFunction<'HbtPortfolioManagementPage', undefined>;
